import { PropertyTypes } from "../../../../enums/PropertyTypes";
import { ISchema, ISchemaAction, ISchemaChild, ISchemaFilter, ISchemaProperty } from "../Schema";
import { EntityNames } from "../../../../enums/EntityNames";
import { EntityViewTypes } from "../../../../enums/EntityViewTypes";
import SidePanelPackingBox from "../../layout/SidePanel/layouts/SidePanelPackingBox";
import { FilterTypes } from "../../../../enums/FilterTypes";
import { FilterMatchType } from "../../../../enums/FilterMatchType";
import { ActionTypes } from "../../../../enums/ActionTypes";
import { ActionStyles } from "../../../../enums/ActionStyles";
import editIcon from "../../../../assets/icons/icon-pen-white.svg"
import { computeV } from "../../../../helpers/SchemaComputeFunction";
import { OuterCubeTypeSmartEnum } from "../../../../enums/Domain/OuterCubeTypeSmartEnum";

export const PackingBoxSchema: ISchema = {
    displayName: "Packing Box",
    GetSidePanelComponent: (data, onViewPage) => <SidePanelPackingBox data={data} onViewPage={onViewPage} />,
    props: [
        {
            name: 'id',
            type: PropertyTypes.string,
            key: true
        } as ISchemaProperty,
        {
            name: 'reference',
            displayName: 'Reference',
            descriptor: true,
            type: PropertyTypes.string,
            key: false,
            showInList: true,
            showInForm: false,
            showInView: false,
            requiredInForm: false,
        } as ISchemaProperty,
        {
            name: 'accountId',
            displayName: 'Belongs to',
            type: PropertyTypes.selectByEntityName,
            key: false,
            showInList: false,
            showInForm: true,
            showInView: false,
            requiredInForm: true,
            entityName: EntityNames.accounts,
            rememberLastFormValue: true,
            children: [],
            renderBasedOnParentData: (values, isCreate) => {
                if(isCreate === false) {
                    return false;
                }
                if(!values) {
                    return false;
                }
                return values.type.value === OuterCubeTypeSmartEnum.Return.value;
            },
            requiredInFormIf: (values) => {
                if(!values) {
                    return false;
                }
                return values.type.value === OuterCubeTypeSmartEnum.Return.value;
            }
        } as ISchemaProperty,
        {
            name: 'belongsTo',
            displayName: 'Belongs to',
            type: PropertyTypes.string,
            key: false,
            showInList: true,
            showInForm: false,
            showInView: false,
            requiredInForm: false,
            entityName: EntityNames.accounts,
            rememberLastFormValue: true,
        } as ISchemaProperty,
        {
            name: 'status',
            displayName: 'Status',
            type: PropertyTypes.status,
            key: false,
            showInList: true,
            showInForm: false,
            showInView: false,
            requiredInForm: false,
        } as ISchemaProperty,
        {
            name: 'currentLocation',
            displayName: 'Current Location',
            type: PropertyTypes.breadcrumb,
            key: false,
            showInList: false,
            showInForm: false,
            showInView: false,
            requiredInForm: false,
        } as ISchemaProperty,
        {
            name: 'typeId',
            displayName: 'Contents type',
            type: PropertyTypes.PackingBoxType,
            key: false,
            showInList: false,
            showInForm: false,
            showInView: false,
            requiredInForm: false,
            entityName: EntityNames.caseMaterialTypes,
            rememberLastFormValue: true
        } as ISchemaProperty,
        {
            name: 'typeName',
            displayName: 'Contents',
            type: PropertyTypes.string,
            key: false,
            showInList: true,
            showInForm: false,
            showInView: true,
            requiredInForm: false,
        } as ISchemaProperty,
        {
            name: 'sequenceNumber',
            displayName: 'Sequence number',
            type: PropertyTypes.int,
            key: false,
            showInList: true,
            showInForm: false,
            showInView: true,
            requiredInForm: false,
        } as ISchemaProperty,
        {
            name: 'volume',
            displayName: 'Volume',
            type: PropertyTypes.string,
            key: false,
            showInList: true,
            showInForm: false,
            showInView: false,
            requiredInForm: false,
            entityName: EntityNames.volumes
        } as ISchemaProperty,
        {
            name: 'vNumberStandardSize',
            displayName: 'Standard Size',
            type: PropertyTypes.vNumberStandardSize,
            key: false,
            showInList: false,
            showInForm: true,
            showInView: false,
            requiredInForm: false,
            className: "small-12 medium-12",
            rememberLastFormValue: false
        } as ISchemaProperty,
        {
            name: 'width',
            displayName: 'Width (cm)',
            type: PropertyTypes.number,
            key: false,
            showInList: false,
            showInForm: false,
            showInView: false,
            requiredInForm: true,
            className: "small-12 medium-4",
            rememberLastFormValue: true
        } as ISchemaProperty,    
        {
            name: 'height',
            displayName: 'Height (cm)',
            type: PropertyTypes.number,
            key: false,
            showInList: false,
            showInForm: false,
            showInView: false,
            requiredInForm: true,
            className: "small-12 medium-4",
            rememberLastFormValue: true
        } as ISchemaProperty,     
        {
            name: 'depth',
            displayName: 'Depth (cm)',
            type: PropertyTypes.number,
            key: false,
            showInList: false,
            showInForm: false,
            showInView: false,
            requiredInForm: true,
            className: "small-12 medium-4",
            rememberLastFormValue: true
        } as ISchemaProperty,
        {
            name: 'computedVSize',
            displayName: 'Computed V size',
            type: PropertyTypes.computed,
            key: false,
            showInList: false,
            showInForm: true,
            showInView: false,
            requiredInForm: false,
            className: "small-12 medium-3",
            computeFunction: ((x : number[]) => computeV([...x])) as Function,
            computeProperties: ["width", "height", "depth"],
            rememberLastFormValue: true
        } as ISchemaProperty
    ],

    children:[ 
        {entityName: EntityNames.ranges, displayName: "Ranges", viewType: EntityViewTypes.list, enableFilters: true} as ISchemaChild,
        {entityName: EntityNames.cases, displayName: "Cases", viewType: EntityViewTypes.list, enableFilters: true} as ISchemaChild,
        {entityName: EntityNames.specimens, displayName: "Specimens", viewType: EntityViewTypes.list, enableFilters: true} as ISchemaChild,
        {entityName: EntityNames.audits, displayName: "Audit Trail", viewType: EntityViewTypes.list, cantView: true} as ISchemaChild,
    ],

    primaryFilter: {
        PropertyName: "Name",
        displayName: "Search",
        type: FilterTypes.search,
        required: false,
        className: "width--full",
        matchType: FilterMatchType.Equal,
        propertyType: PropertyTypes.string,
        placeHolder: "Enter a PB reference"
    } as ISchemaFilter,

    filters: [],

    sidePanelActions: [
        {text: "Edit Details", action: ActionTypes.edit, style: ActionStyles.primary, icon: editIcon } as ISchemaAction,
        {text: "Delete", action: ActionTypes.delete, style: ActionStyles.destructive } as ISchemaAction
    ]
}
